import React, { useCallback, useEffect, useState } from "react";
import ApiTokensCard from "../components/ApiTokensCard";
import { Link } from "react-router-dom";
import api from "../api/client";

/**
 * Integrations hub — one place for every integration. The top section is a
 * catalog of MSP vendor integrations (rendered generically from the backend
 * /integrations/catalog field schema; credentials are write-only). The bottom
 * section links to the platform integrations that keep their own dedicated
 * screens (PSA, M365 tenants, network device APIs, cloud storage, SSO, etc.).
 */

interface Field {
  key: string;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  help?: string;
}
interface CatalogItem {
  slug: string;
  name: string;
  category: string;
  blurb: string;
  has_sync: boolean;
  fields: Field[];
  configured: boolean;
  enabled: boolean;
  last_sync_at?: string | null;
  last_sync_status?: string | null;
  last_sync_error?: string | null;
}

const PLATFORM_TILES: { to: string; name: string; blurb: string }[] = [
  { to: "/psa-integrations", name: "External PSA", blurb: "ConnectWise, Autotask, HaloPSA — company + ticket sync." },
  { to: "/m365-tenants", name: "Microsoft 365 tenants", blurb: "Per-customer M365/Entra tenant connections." },
  { to: "/network-discovery", name: "Network device APIs", blurb: "Meraki, UniFi, FortiGate and more — per customer." },
  { to: "/cloud-storage", name: "Cloud storage", blurb: "Azure / AWS / GCP object storage, per customer." },
  { to: "/inbound-channels", name: "Slack / Teams", blurb: "Create tickets from chat slash-commands." },
  { to: "/integrations/sso", name: "Single sign-on (SSO)", blurb: "OIDC / SAML staff sign-in providers." },
  { to: "/integrations/webhooks", name: "Outbound webhooks", blurb: "Slack, Teams, PagerDuty, generic JSON alerts." },
];

function StatusBadge({ item }: { item: CatalogItem }) {
  if (!item.configured)
    return (
      <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
        Not configured
      </span>
    );
  const fail = item.last_sync_status === "failure";
  return (
    <span
      className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${
        fail
          ? "bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800"
          : "bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
      }`}
    >
      {fail ? "Sync error" : "Connected"}
    </span>
  );
}

export default function Integrations() {
  const [categoryLabels, setCategoryLabels] = useState<Record<string, string>>({});
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [editing, setEditing] = useState<CatalogItem | null>(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    api
      .get("/api/v1/integrations/catalog")
      .then((r) => {
        setCategoryLabels(r.data.category_labels || {});
        setItems(r.data.integrations || []);
      })
      .catch(() => setItems([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (slug: string, action: "test" | "sync" | "disconnect") => {
    setMsg("");
    try {
      if (action === "disconnect") {
        if (!window.confirm("Remove the stored credentials for this integration?")) return;
        await api.delete(`/api/v1/integrations/${slug}`);
        setMsg("Disconnected.");
      } else {
        const r = await api.post(`/api/v1/integrations/${slug}/${action}`);
        setMsg(r.data?.message || (action === "sync" ? "Sync started." : "Tested."));
      }
      load();
    } catch (e: unknown) {
      const x = e as { response?: { data?: { detail?: string } } };
      setMsg(x.response?.data?.detail || `${action} failed`);
    }
  };

  const byCategory = (cat: string) => items.filter((i) => i.category === cat);
  const cats = Object.keys(categoryLabels).filter((c) => byCategory(c).length > 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Integrations</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Connect the tools your MSP runs on. Credentials are encrypted at rest and never shown
          again after saving.
        </p>
      </div>

      {msg && <div className="mb-4 bg-blue-50 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 text-sm px-4 py-2 rounded-lg">{msg}</div>}

      {/* Platform integrations with their own screens */}
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Platform integrations</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {PLATFORM_TILES.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className="block bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 hover:border-blue-300 dark:hover:border-blue-500 hover:shadow"
          >
            <div className="font-medium text-gray-800 dark:text-gray-100">{t.name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.blurb}</div>
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">Configure →</div>
          </Link>
        ))}
      </div>

      {/* Vendor catalog (generic credential storage) */}
      {cats.map((cat) => (
        <div key={cat} className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{categoryLabels[cat]}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {byCategory(cat).map((item) => (
              <div key={item.slug} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-gray-800 dark:text-gray-100">{item.name}</div>
                  <StatusBadge item={item} />
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex-1">{item.blurb}</div>
                {!item.has_sync && (
                  <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                    Credential storage now; live sync coming soon.
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  <button
                    onClick={() => setEditing(item)}
                    className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg px-2.5 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    {item.configured ? "Reconfigure" : "Configure"}
                  </button>
                  {item.configured && (
                    <>
                      <button
                        onClick={() => act(item.slug, "test")}
                        className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg px-2.5 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      >
                        Test
                      </button>
                      {item.has_sync && (
                        <button
                          onClick={() => act(item.slug, "sync")}
                          className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg px-2.5 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                        >
                          Sync
                        </button>
                      )}
                      <button
                        onClick={() => act(item.slug, "disconnect")}
                        className="border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg px-2.5 py-1 hover:bg-red-50 dark:hover:bg-red-900/30"
                      >
                        Disconnect
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Inbound API access, folded in here rather than given its own sidebar
          item. Everything above is a credential AeroGauge uses to call OUT to a
          vendor; these are credentials something else uses to call IN to this
          appliance's API (scripts, CI, the AI terminal). Same page because both
          are integration plumbing — no menu entry because one line in the
          sidebar for it was more prominent than it deserves. */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Inbound API access
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Tokens for scripts, CI and the AI terminal to call this appliance's API.
        </p>
        <ApiTokensCard />
      </div>

      {editing && (
        <ConfigModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setMsg(`Saved ${editing.name}.`);
            load();
          }}
        />
      )}
    </div>
  );
}

function ConfigModal({
  item,
  onClose,
  onSaved,
}: {
  item: CatalogItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const requiredOk = item.fields
    .filter((f) => f.required !== false)
    .every((f) => (values[f.key] || "").trim());

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const credentials: Record<string, string> = {};
      for (const f of item.fields) {
        const v = (values[f.key] || "").trim();
        if (v) credentials[f.key] = v;
      }
      await api.put(`/api/v1/integrations/${item.slug}`, { credentials });
      onSaved();
    } catch (e: unknown) {
      const x = e as { response?: { data?: { detail?: string } } };
      setError(x.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">Configure {item.name}</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-sm">
            ✕
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{item.blurb}</p>
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
            {error}
          </p>
        )}
        <div className="space-y-3">
          {item.fields.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                {f.label}
                {f.required === false && <span className="text-gray-400 dark:text-gray-500 font-normal"> (optional)</span>}
              </label>
              <input
                type={f.type === "password" ? "password" : "text"}
                value={values[f.key] || ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
              />
              {f.help && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{f.help}</p>}
            </div>
          ))}
        </div>
        {item.configured && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3">
            Credentials are write-only — fields are blank. Re-enter to update.
          </p>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !requiredOk}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
